import React, { useState } from "react";
import { FaTrashCan, FaPen, FaShare } from "react-icons/fa6";
import { IoCalendarOutline, IoTimeOutline, IoPersonOutline, IoMailOutline } from "react-icons/io5";
import AddServicePopup from "../AddServicePopup";
import { toast } from "react-toastify";
import SharePopup from "../../../../PopUp/SocialSharePopup/SharePopup";
import { useNavigate } from "react-router-dom";

const EventCard = ({ event, isAdmin, onDelete, onUpdate, communityId, username }) => {
  const [showAddService, setShowAddService] = useState(false);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [socialUrl, setSocialUrl] = useState('');
  const navigate = useNavigate();
  const [showBookingsPopup, setShowBookingsPopup] = useState(false);

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to disable this event?')) {
      onDelete(event._id);
    }
  };

  const handleOpenSocialShare = () => {
    // Set the URL you want to share
    setSocialUrl(`${window.location.origin}/meeting/schedule/${username}/${event._id}`);
    setSharePopupOpen(true);
  };

  const handleCardClick = () => {
    navigate(`/meeting/schedule/${username}/${event._id}`);
  };

  const formatDateTime = (dateStr, timeStr) => {
    const date = new Date(timeStr);
    return `${dateStr} at ${date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    })}`;
  };

  const getPaymentStatusDisplay = (status) => {
    switch(status) {
      case 'Not Required':
      case 'Paid':
        return 'Confirmed';
      case 'Not Paid':
        return 'Payment Pending';
      case 'Failed':
        return 'Payment Failed';
      default:
        return status;
    }
  };

  const getPaymentStatusClass = (status) => {
    switch(status) {
      case 'Not Required':
      case 'Paid':
        return 'confirmed';
      case 'Not Paid':
        return 'pending';
      case 'Failed':
        return 'failed';
      default:
        return '';
    }
  };

  return (
    <>
      <div 
        className="ch-event-card" 
        onClick={handleCardClick}
        style={{ cursor: 'pointer' }}
      >
        {/* Admin Actions */}
        {isAdmin && (
          <div className="admin-actions" onClick={e => e.stopPropagation()}>
            <button 
              className="edit-button" 
              onClick={(e) => {
                e.stopPropagation();
                setShowAddService(true);
              }}
            >
              <FaPen size={20} />
            </button>
            <button 
              className="delete-button" 
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
            >
              <FaTrashCan size={20} />
            </button>
          </div>
        )}

        {/* Share Button */}
        <div className="card-actions" onClick={e => e.stopPropagation()}>
          <button 
            className="share-button" 
            onClick={(e) => {
              e.stopPropagation();
              handleOpenSocialShare();
            }}
          >
            <FaShare size={20} />
          </button>
        </div>

        <div className="ch-event-info">
          <h3>{event.title}</h3>
          <div
            className="ch-event-description"
            dangerouslySetInnerHTML={{ __html: event.description }}
          />

          <div className="ch-event-meta">
            <div className="ch-event-details">
              <span>
                <IoCalendarOutline size={20} />
              </span>
              <span>{event.duration} mins</span>
              <span className="ch-separator">|</span>
              <span>{event.bookings?.length || 0} Bookings</span>
            </div>
          </div>
        </div>

        <div className="ch-event-actions">
          <div className="ch-actions-left">
            {isAdmin ? (
              <div 
                className="ch-bookings-count"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBookingsPopup(true);
                }}
              >
                View All Bookings
              </div>
            ) : (
              <div className="ch-bookings-count" onClick={handleCardClick}>
                Book Now
              </div>
            )}
          </div>
          <div className="ch-actions-right">
            <div className="ch-event-price">
              <div className="ch-price-tag">
                {event.price > 0 ? (
                  <>
                    {event.discount > 0 ? (
                      <>
                        <span className="ch-original-price">₹{event.price.toFixed(0)}</span>
                        <span className="ch-final-price">
                          ₹{(event.price * (1 - event.discount / 100)).toFixed(0)}
                        </span>
                      </>
                    ) : (
                      <span className="ch-final-price">
                        ₹{event.price.toFixed(0)}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="ch-final-price free">Free</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Replace EventModal with AddServicePopup */}
      {showAddService && (
        <AddServicePopup
          communityId={communityId}
          onClose={() => setShowAddService(false)}
          onServiceAdded={onUpdate}
          editingService={{
            ...event,
            type: 'event',
            eventType: 'Private'
          }}
        />
      )}

      {/* Share Popup */}
      {sharePopupOpen && (
        <SharePopup
          postId={event._id}
          isOpen={sharePopupOpen}
          setIsOpen={setSharePopupOpen}
          url={socialUrl}
        />
      )}

      {/* Bookings Popup */}
      {showBookingsPopup && (
        <div className="bookings-popup-overlay" onClick={() => setShowBookingsPopup(false)}>
          <div className="bookings-popup-content" onClick={e => e.stopPropagation()}>
            <div className="bookings-popup-header">
              <h3>Booking Details</h3>
              <button className="close-button" onClick={() => setShowBookingsPopup(false)}>×</button>
            </div>
            <div className="bookings-list">
              {event.bookings?.map((booking, index) => (
                <div key={booking._id} className="booking-item">
                  <div className="booking-header">
                    <h4>Booking #{index + 1}</h4>
                    <span className={`payment-status ${getPaymentStatusClass(booking.paymentStatus)}`}>
                      {getPaymentStatusDisplay(booking.paymentStatus)}
                    </span>
                  </div>
                  <div className="booking-details">
                    <div className="detail-row">
                      <IoPersonOutline />
                      <span>{booking.name}</span>
                    </div>
                    <div className="detail-row">
                      <IoMailOutline />
                      <span>{booking.email}</span>
                    </div>
                    <div className="detail-row">
                      <IoCalendarOutline />
                      <span>{formatDateTime(booking.date, booking.startTime)}</span>
                    </div>
                    <div className="detail-row">
                      <IoTimeOutline />
                      <span>Duration: {
                        Math.round((new Date(booking.endTime) - new Date(booking.startTime)) / 60000)
                      } mins</span>
                    </div>
                    {booking.additionalInfo && (
                      <div className="additional-info">
                        <strong>Additional Info:</strong>
                        <p>{booking.additionalInfo}</p>
                      </div>
                    )}
                    {booking.meetingLink && (
                      <a 
                        href={booking.meetingLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="meeting-link"
                      >
                        Join Meeting
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventCard;
